import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export type QA = {
  q: string;
  a: string;
};

export const faqItems: QA[] = [
  {
    q: 'How do you price projects?',
    a: 'Project-based, in scoped phases. After a discovery call we write a fixed-scope spec for the first phase with a fixed price and a delivery window. Long projects ship as a sequence of phases, each one fully usable on its own — you never pay for vapor.',
  },
  {
    q: "What's a typical timeline?",
    a: 'Small, well-defined projects (an integration, a focused internal tool) ship in 3–6 weeks. Mid-sized SaaS modules typically run 8–12 weeks per phase. Full platforms like PharmaVision or MunicipalOS are multi-phase engagements measured in months, with a working slice live in under four weeks.',
  },
  {
    q: 'Who actually writes the code?',
    a: 'The same person you talk to in the discovery call. No layered hand-offs, no junior account manager standing between you and the engineer. The team is intentionally small for exactly this reason.',
  },
  {
    q: 'Do I own the code and the IP?',
    a: 'Yes. You own the source, the database schema, the deployment, and any custom integrations. We hand over the repo and the infrastructure access at the end of the engagement — nothing is locked in our accounts.',
  },
  {
    q: 'Do you sign NDAs?',
    a: 'Yes. If you need to share anything sensitive before we have a contract, we sign an NDA before the first call. Mention it in the contact form and we send one over.',
  },
  {
    q: 'What happens after launch — do you maintain it?',
    a: 'If you want us to, yes. We offer ongoing support and continuous engineering retainers for clients who want their software to keep evolving. If you prefer to hand it off to your own team, we leave it documented and easy to pick up.',
  },
  {
    q: 'How custom is "custom"?',
    a: 'Custom means designed around your actual workflow, not a configuration of someone else\'s product. We pick the boring, durable parts of the stack (Laravel, React, MySQL/PostgreSQL) so the codebase is something a future developer can read — but the product logic on top is yours, shaped to what your team actually does.',
  },
  {
    q: "What if I'm not technical?",
    a: 'Most of our best clients aren\'t. The first call is in plain language — no jargon, no condescension. We translate between what your business needs and what the engineering decisions are, and we explain trade-offs in terms you can actually use to make a call.',
  },
  {
    q: 'Can you integrate with our existing systems?',
    a: 'Yes — that\'s a big part of what we do. ERPs, CRMs, payment gateways, accounting software, custom internal services, third-party APIs (Stripe, Booking, Shopify, the national HIF, etc.). We design the integration layer so data flows reliably end to end and the seams don\'t leak.',
  },
  {
    q: 'Where are you based / what time zone?',
    a: 'Skopje, North Macedonia (Central European Time). We work with teams across Europe, the US, and beyond — async-first by default, with scheduled syncs at times that suit you.',
  },
];

function Item({ item, isOpen, onToggle }: { item: QA; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] transition hover:border-white/20">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6 md:py-5"
        aria-expanded={isOpen}
      >
        <span className="font-display text-base font-semibold text-white md:text-lg">
          {item.q}
        </span>
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition ${
            isOpen
              ? 'border-accent-400/60 bg-accent-500/15 text-accent-400'
              : 'border-white/15 bg-white/5 text-white/70'
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-45' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-sm leading-relaxed text-white/70 md:px-6 md:pb-6 md:text-base">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="container-page py-20 md:py-28">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="max-w-2xl"
      >
        <p className="eyebrow">Frequently asked</p>
        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
          Answers to the questions you'd ask in the first call.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-white/65 md:text-lg">
          No funnel, no boilerplate — the same answers you'd get on a discovery call,
          written down.
        </p>
      </motion.div>

      <div className="mt-10 grid items-start gap-3 md:grid-cols-2 md:gap-4">
        {faqItems.map((item, i) => (
          <Item
            key={item.q}
            item={item}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
          />
        ))}
      </div>
    </section>
  );
}
